/**
 * TaskPortalTaskService
 * Service for main task operations in the portal.
 * Route: api/task-portal/tasks
 */
import { TaskPortalBaseService } from './TaskPortalBaseService';
/**
 * TaskPortalTaskService
 * Handles main task operations for the task portal.
 * Maps to /api/task-portal/tasks endpoint
 */
export class TaskPortalTaskService extends TaskPortalBaseService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? TaskPortalTaskService.DEFAULT_SERVICE_PATH,
            basePath: config?.basePath ?? TaskPortalTaskService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    // ==================== LIST OPERATIONS ====================
    /**
     * Get all tasks where user is creator or assignee
     * GET /api/task-portal/tasks
     */
    async getMyTasks(filter) {
        const response = await this.getMyTasksRaw(filter);
        return response.data ?? [];
    }
    /**
     * Get all tasks (raw response)
     */
    getMyTasksRaw(filter) {
        const url = this.buildUrlWithParams('', filter);
        return this.http.get(url);
    }
    /**
     * Get tasks created by the current user
     * GET /api/task-portal/tasks/created
     */
    async getCreatedTasks(filter) {
        const response = await this.getCreatedTasksRaw(filter);
        return response.data ?? [];
    }
    /**
     * Get created tasks (raw response)
     */
    getCreatedTasksRaw(filter) {
        const url = this.buildUrlWithParams('created', filter);
        return this.http.get(url);
    }
    /**
     * Get tasks assigned to the current user
     * GET /api/task-portal/tasks/assigned
     */
    async getAssignedTasks(filter) {
        const response = await this.getAssignedTasksRaw(filter);
        return response.data ?? [];
    }
    /**
     * Get assigned tasks (raw response)
     */
    getAssignedTasksRaw(filter) {
        const url = this.buildUrlWithParams('assigned', filter);
        return this.http.get(url);
    }
    /**
     * Get tasks the current user is looped in on
     * GET /api/task-portal/tasks/looped-in
     */
    async getLoopedInTasks() {
        const response = await this.getLoopedInTasksRaw();
        return response.data ?? [];
    }
    /**
     * Get looped-in tasks (raw response)
     */
    getLoopedInTasksRaw() {
        return this.http.get(this.buildUrlWithParams('looped-in'));
    }
    // ==================== GET SINGLE TASK ====================
    /**
     * Get a task by ID
     * Note: For assignees, automatically updates StatusID from 0 to 1 on first view
     * GET /api/task-portal/tasks/{taskId}
     */
    async getTask(taskId) {
        const response = await this.getTaskRaw(taskId);
        if (!response.data) {
            throw new Error(`Task ${taskId} not found`);
        }
        return response.data;
    }
    /**
     * Get task (raw response)
     */
    getTaskRaw(taskId) {
        return this.get(String(taskId));
    }
    // ==================== UPDATE OPERATIONS ====================
    /**
     * Update task details (creator only)
     * PUT /api/task-portal/tasks/{taskId}
     */
    async updateTask(taskId, data) {
        const response = await this.updateTaskRaw(taskId, data);
        if (!response.data) {
            throw new Error('Failed to update task');
        }
        return response.data;
    }
    /**
     * Update task (raw response)
     */
    updateTaskRaw(taskId, data) {
        return this.put(String(taskId), data);
    }
    /**
     * Update task status (creator or assignee)
     * PATCH /api/task-portal/tasks/{taskId}/status
     *
     * @param completionNotes Completion notes — required when marking a task as
     *   "Done It" (statusId 3); ignored for other statuses.
     */
    async updateStatus(taskId, statusId, completionNotes) {
        const response = await this.updateStatusRaw(taskId, { statusID: statusId, completionNotes });
        if (!response.data) {
            throw new Error('Failed to update status');
        }
        return response.data;
    }
    /**
     * Update status (raw response)
     */
    updateStatusRaw(taskId, data) {
        return this.patch(`${taskId}/status`, data);
    }
    /**
     * Update task progress stage (creator or assignee)
     * PATCH /api/task-portal/tasks/{taskId}/progress
     */
    async updateProgress(taskId, progressStageId) {
        const response = await this.updateProgressRaw(taskId, { progressStageID: progressStageId });
        if (!response.data) {
            throw new Error('Failed to update progress');
        }
        return response.data;
    }
    /**
     * Update progress (raw response)
     */
    updateProgressRaw(taskId, data) {
        return this.patch(`${taskId}/progress`, data);
    }
    /**
     * Update task priority (creator only)
     * PATCH /api/task-portal/tasks/{taskId}/priority
     */
    async updatePriority(taskId, priorityId) {
        const response = await this.updatePriorityRaw(taskId, { priorityID: priorityId });
        if (!response.data) {
            throw new Error('Failed to update priority');
        }
        return response.data;
    }
    /**
     * Update priority (raw response)
     */
    updatePriorityRaw(taskId, data) {
        return this.patch(`${taskId}/priority`, data);
    }
    // ==================== DELETE OPERATIONS ====================
    /**
     * Delete a task (creator only)
     * DELETE /api/task-portal/tasks/{taskId}
     */
    async deleteTask(taskId) {
        const response = await this.deleteTaskRaw(taskId);
        if (!response.success) {
            throw new Error('Failed to delete task');
        }
    }
    /**
     * Delete task (raw response)
     */
    deleteTaskRaw(taskId) {
        return this.delete(String(taskId));
    }
    // ==================== ACTIVITY LOGS ====================
    /**
     * Get enriched activity logs for a task the user has access to.
     * GET /api/task-portal/tasks/{taskId}/logs
     *
     * Pass `skip`/`take` to page through the activity history 10-or-so at a time
     * (the backend default page size is 10). Omit both to fetch the full list,
     * which matches the original behaviour.
     */
    async getTaskLogs(taskId, skip, take) {
        const response = await this.getTaskLogsRaw(taskId, skip, take);
        return response.data ?? [];
    }
    getTaskLogsRaw(taskId, skip, take) {
        const qs = [];
        if (typeof skip === 'number')
            qs.push(`skip=${skip}`);
        if (typeof take === 'number')
            qs.push(`take=${take}`);
        const suffix = qs.length ? `?${qs.join('&')}` : '';
        return this.get(`${taskId}/logs${suffix}`);
    }
}
TaskPortalTaskService.DEFAULT_SERVICE_PATH = 'tasks';
TaskPortalTaskService.BASE_PATH = 'api/task-portal/tasks';
//# sourceMappingURL=TaskPortalTaskService.js.map